import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Badge,
  FormMobilePhoneInput,
  FormSubmitButton,
  FormTextInput,
  GlobalButton,
  NavigateLinks,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { Save as SaveIcon } from '@mui/icons-material';
import { toMobilePhoneValue } from '@/utils/phoneNumber';

import { useHeadquartersOptions } from '../../../LogisticOperator/hooks';
import { UserAccessScopeSection, UserRoles } from '../../components';
import {
  defaultEditUserWithAccessValues,
  defaultUserAccessScopeValues,
  buildEditUserWithAccessSchema,
  EditUserWithAccessSchema,
} from '../../models';
import { childrenUsersPageBreadcrumbs } from '../../config';
import {
  useReplaceUserAccessScopes,
  useUser,
  useUserActions,
  useUserScopeLists,
} from '../../hooks';
import { mapScopesToAccessForm } from '../../utils/mapScopesToAccessForm';
import {
  COMMERCE_ROLE_NAMES,
  EXTERNAL_OPERATOR_ROLE_NAMES,
} from '@/components/security/AppRoles';
import { RoleRestriction } from '../../enums';

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, isLoading, error } = useUser({ userId: id || '' });
  const { onUpdateUser, isUpdatingUser } = useUserActions();
  const { replaceUserAccessScopes, isReplacingAccess } =
    useReplaceUserAccessScopes();

  const { options: headquarterOptions, isLoading: isLoadingHq } =
    useHeadquartersOptions();

  const {
    scopes,
    exclusions,
    isLoading: isLoadingScopes,
    isError: isScopeError,
  } = useUserScopeLists(user?.id);

  const hqSet = useMemo(() => {
    const set = new Set<string>(headquarterOptions.map((o) => o.value));
    for (const scope of scopes) {
      if (scope.scopeType !== 'operator' || !scope.scopeId) continue;
      if (scope.operatorHeadquarter == null) continue;
      if (scope.operatorHeadquarter) set.add(scope.scopeId);
      else set.delete(scope.scopeId);
    }
    return set;
  }, [headquarterOptions, scopes]);

  const accessPayload = useMemo(() => {
    if (!user?.id || isLoadingScopes || isLoadingHq) {
      return undefined;
    }
    if (isScopeError) {
      return defaultUserAccessScopeValues;
    }
    return mapScopesToAccessForm(scopes, exclusions, hqSet);
  }, [
    user?.id,
    isLoadingScopes,
    isLoadingHq,
    isScopeError,
    scopes,
    exclusions,
    hqSet,
  ]);

  const breadcrumbs = useMemo(
    () => childrenUsersPageBreadcrumbs(navigate),
    [navigate]
  );

  const editedUserType = useMemo(() => {
    const roleNames = (user?.userRoles ?? []).map(
      (userRole) => userRole.role.name
    );
    if (roleNames.some((name) => COMMERCE_ROLE_NAMES.includes(name))) {
      return RoleRestriction.COMMERCE;
    }
    if (roleNames.some((name) => EXTERNAL_OPERATOR_ROLE_NAMES.includes(name))) {
      return RoleRestriction.EXTERNAL_OPERATOR;
    }
    return RoleRestriction.OPERATOR;
  }, [user?.userRoles]);

  const formAccessPayload = useMemo(() => {
    if (
      accessPayload === undefined ||
      editedUserType !== RoleRestriction.EXTERNAL_OPERATOR
    ) {
      return accessPayload;
    }
    const operatorIds = scopes
      .filter((scope) => scope.scopeType === 'operator' && scope.scopeId)
      .map((scope) => scope.scopeId as string);
    return {
      ...accessPayload,
      operators: {
        ...accessPayload.operators,
        includeAll: false,
        selected: operatorIds,
        excluded: [],
        subOperators: { includeAll: false, selected: [], excluded: [] },
      },
    };
  }, [accessPayload, editedUserType, scopes]);

  const editUserSchema = useMemo(
    () => buildEditUserWithAccessSchema(editedUserType),
    [editedUserType]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditUserWithAccessSchema>({
      resolver: zodResolver(editUserSchema),
      defaultValues: defaultEditUserWithAccessValues,
      mode: 'onChange',
    });

  useEffect(() => {
    if (!user?.id || formAccessPayload === undefined) {
      return;
    }
    reset({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
      phoneNumber: toMobilePhoneValue(user.phoneNumber),
      operators: formAccessPayload.operators,
      commerces: formAccessPayload.commerces,
      branches: formAccessPayload.branches,
    });
  }, [
    user?.id,
    user?.firstName,
    user?.lastName,
    user?.email,
    user?.phoneNumber,
    user?.updatedAt,
    formAccessPayload,
    reset,
  ]);

  const onSubmit = async (data: EditUserWithAccessSchema) => {
    if (!id) return;
    const { operators, commerces, branches, ...personal } = data;
    const personalOk = await onUpdateUser(id, personal);
    if (!personalOk) return;
    const accessOk = await replaceUserAccessScopes(
      id,
      {
        operators,
        commerces,
        branches,
      },
      { existingScopes: scopes, existingExclusions: exclusions }
    );
    if (accessOk) {
      navigate(-1);
    }
  };

  const isAccessReady = Boolean(user?.id && accessPayload !== undefined);

  const branches = watch('branches');
  const isMissingSubOperatorBranch =
    editedUserType === RoleRestriction.EXTERNAL_OPERATOR &&
    !!branches?.requiredForSubOperator &&
    branches.selected.length === 0;

  if (!id) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Editar usuario' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          El ID del usuario es requerido
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Editar usuario' breadcrumbs={breadcrumbs} />
        <div className='text-center text-gray-500 py-8'>Cargando...</div>
      </section>
    );
  }

  if (error || !user?.id) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Editar usuario' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          Error al cargar el usuario o usuario no encontrado
        </div>
      </section>
    );
  }

  const fullName = `${user.firstName} ${user.lastName}`.trim();
  const isActive = user.isActive ?? user.active ?? false;

  return (
    <section className='flex flex-col w-full h-full gap-10'>
      <NavigateLinks
        actualPage={fullName ? `Editar: ${fullName}` : 'Editar usuario'}
        breadcrumbs={breadcrumbs}
      />
      <form
        className='flex flex-col gap-5 p-8 mx-auto w-full max-w-[1400px]'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-6 lg:flex-row lg:items-stretch'>
            <div className='min-w-0 flex-1 bg-white rounded-lg p-6 border border-gray-200 shadow-sm'>
              <div className='flex flex-wrap items-center justify-between gap-4 mb-4'>
                <h3 className='text-lg font-semibold text-gray-800'>
                  Información del usuario
                </h3>
                <div className='w-fit'>
                  <Badge
                    value={isActive ? 'Activo' : 'Inactivo'}
                    backgroundColor={isActive ? '#dcfce7' : '#f3f4f6'}
                    textColor={isActive ? '#166534' : '#374151'}
                    showBall={false}
                  />
                </div>
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <FormTextInput
                  name='firstName'
                  placeholder='Nombre'
                  control={control}
                  required
                  autoFocus
                />
                <FormTextInput
                  name='lastName'
                  placeholder='Apellido'
                  control={control}
                  required
                />
                <FormTextInput
                  name='email'
                  placeholder='Correo electrónico'
                  control={control}
                  required
                />
                <FormMobilePhoneInput
                  name='phoneNumber'
                  control={control}
                  required
                />
              </div>
            </div>

            <div className='w-full lg:max-w-md lg:shrink-0 flex'>
              <div className='rounded-lg bg-white p-6 border border-gray-200 shadow-sm flex flex-col w-full'>
                <h3 className='mb-4 text-lg font-semibold text-gray-800'>
                  Roles
                </h3>
                <UserRoles userRoles={user.userRoles} />
              </div>
            </div>
          </div>

          <div>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>
              Operadores, comercios y sucursales
            </h2>
            {!isAccessReady && (
              <p className='text-sm text-gray-500 mb-4'>
                Cargando alcance actual del usuario…
              </p>
            )}
            {isScopeError && isAccessReady && (
              <p className='text-sm text-amber-700 mb-4'>
                No se pudo cargar el alcance desde el servidor. Podés editar y
                guardar para reemplazarlo, o reintentar recargando la página.
              </p>
            )}
            <div
              className={
                !isAccessReady ? 'pointer-events-none opacity-50' : undefined
              }
            >
              <UserAccessScopeSection
                control={control}
                watch={watch}
                setValue={setValue}
                userType={editedUserType}
                operatorsError={errors.operators?.selected?.message}
                branchesError={errors.branches?.selected?.message}
              />
            </div>
          </div>
        </div>

        <div className='flex flex-wrap justify-end gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => navigate(-1)}
          >
            Cancelar
          </GlobalButton>
          <FormSubmitButton
            label='Guardar cambios'
            action={handleSubmit(onSubmit)}
            isLoading={isUpdatingUser || isReplacingAccess}
            disabled={!isAccessReady || isMissingSubOperatorBranch}
            icon={<SaveIcon />}
          />
        </div>
      </form>
    </section>
  );
};

export default EditUser;
